import { useState, useMemo, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { DocumentManagementActions } from './admin/DocumentManagementActions';
import { format } from 'date-fns';

const LIST_DOCUMENTS_BY_CASE = gql`
  query ListDocumentsByCase($caseId: ID!, $includeDeleted: Boolean) {
    listDocumentsByCase(caseId: $caseId, includeDeleted: $includeDeleted) {
      id
      fileName
      fileType
      fileSize
      description
      createdAt
      uploadedBy
      uploader {
        id
        firstName
        lastName
      }
      isDeleted
      deletedAt
      deletedBy
    }
  }
`;

const GET_DOCUMENT_DOWNLOAD_URL = gql`
  query GetDocumentDownloadUrl($id: ID!) {
    getDocumentDownloadUrl(id: $id)
  }
`;

interface Document {
  id: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  description?: string;
  createdAt: string;
  uploadedBy?: string;
  uploader?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  isDeleted?: boolean;
  deletedAt?: string;
  deletedBy?: string;
}

interface ListDocumentsByCaseData {
  listDocumentsByCase: Document[];
}

interface GetDocumentDownloadUrlData {
  getDocumentDownloadUrl: string;
}

type SortField = 'fileName' | 'fileType' | 'fileSize' | 'createdAt';
type SortDirection = 'asc' | 'desc';

interface ClientDocumentsTableProps {
  clientCases: any[];
  userRole?: string;
}

// Helper functions defined outside component to avoid hoisting issues
const getFileType = (fileName: string) => {
  const extension = fileName.toLowerCase().split('.').pop();
  if (!extension) return 'Other';
  
  if (extension === 'pdf') return 'PDF';
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(extension)) return 'Image';
  if (['doc', 'docx'].includes(extension)) return 'Word';
  if (['xls', 'xlsx'].includes(extension)) return 'Excel';
  return 'Other';
};

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

export function ClientDocumentsTable({ clientCases, userRole }: ClientDocumentsTableProps) {
  const [allDocuments, setAllDocuments] = useState<any[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [caseFilter, setCaseFilter] = useState<string>('');
  const [fileTypeFilter, setFileTypeFilter] = useState<string>('all');
  const [showDeleted, setShowDeleted] = useState(false);
  
  // Table controls
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  
  // Check if user is admin
  const isAdmin = userRole === 'ADMIN';
  
  const [getDocumentsByCase] = useLazyQuery<ListDocumentsByCaseData>(LIST_DOCUMENTS_BY_CASE);
  const [getDownloadUrl] = useLazyQuery<GetDocumentDownloadUrlData>(GET_DOCUMENT_DOWNLOAD_URL);

  // Fetch documents for all client cases
  const fetchAllDocuments = async () => {
    if (clientCases.length === 0) return;
    
    setLoadingDocs(true);
    const documentsPromises = clientCases.map(async (caseItem: any) => {
      try {
        const { data } = await getDocumentsByCase({
          variables: { 
            caseId: caseItem.id,
            includeDeleted: isAdmin // Only fetch deleted docs if user is admin
          },
        });
        
        const docs = data?.listDocumentsByCase || [];
        
        return docs.map((doc: any) => ({
          ...doc,
          caseId: caseItem.id,
          caseNumber: caseItem.caseNumber,
          caseType: caseItem.caseType,
        }));
      } catch (error) {
        console.error(`Error fetching documents for case ${caseItem.id}:`, error);
        return [];
      }
    });

    const documentsArrays = await Promise.all(documentsPromises);
    const flattenedDocuments = documentsArrays.flat();
    setAllDocuments(flattenedDocuments);
    setLoadingDocs(false);
  };

  useEffect(() => {
    fetchAllDocuments();
  }, [clientCases, isAdmin]); // Re-fetch when admin status changes

  // Get unique file types for filter
  const fileTypes = useMemo(() => {
    const types = new Set<string>();
    allDocuments.forEach((doc: any) => {
      const type = getFileType(doc.fileName);
      types.add(type);
    });
    return Array.from(types).sort();
  }, [allDocuments]);

  // Filtering and sorting
  const filteredDocuments = useMemo(() => {
    let filtered = allDocuments.filter((doc: any) => {
      // Filter deleted documents (unless admin has enabled showDeleted)
      if (!showDeleted && doc.isDeleted) return false;
      
      // Search
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          doc.fileName.toLowerCase().includes(query) ||
          doc.caseNumber.toLowerCase().includes(query) ||
          doc.description?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      
      // Case filter
      if (caseFilter && doc.caseId !== caseFilter) return false;
      
      // File type filter
      if (fileTypeFilter !== 'all') {
        const docType = getFileType(doc.fileName);
        if (docType !== fileTypeFilter) return false;
      }
      
      return true;
    });

    // Sort
    filtered.sort((a: any, b: any) => {
      let aVal, bVal;
      
      switch (sortField) {
        case 'fileName':
          aVal = a.fileName.toLowerCase();
          bVal = b.fileName.toLowerCase();
          break;
        case 'fileType':
          aVal = getFileType(a.fileName).toLowerCase();
          bVal = getFileType(b.fileName).toLowerCase();
          break;
        case 'fileSize':
          aVal = a.fileSize;
          bVal = b.fileSize;
          break;
        case 'createdAt':
          aVal = new Date(a.createdAt).getTime();
          bVal = new Date(b.createdAt).getTime();
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [allDocuments, searchQuery, caseFilter, fileTypeFilter, showDeleted, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setCaseFilter('');
    setFileTypeFilter('all');
    setShowDeleted(false);
  };

  const handleDownload = async (documentId: string) => {
    try {
      const { data } = await getDownloadUrl({
        variables: { id: documentId },
      });

      if (data?.getDocumentDownloadUrl) {
        window.open(data.getDocumentDownloadUrl, '_blank');
      }
    } catch (error: any) {
      console.error('Error downloading document:', error);
      alert('Error downloading document: ' + error.message);
    }
  };

  const getFileIcon = (fileType: string) => {
    const baseClasses = "w-5 h-5";
    const type = getFileType(fileType);
    
    if (type === 'PDF') {
      return (
        <svg className={`${baseClasses} text-red-600`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      );
    }
    if (type === 'Image') {
      return (
        <svg className={`${baseClasses} text-blue-600`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    }
    if (type === 'Word') {
      return (
        <svg className={`${baseClasses} text-blue-700`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    }
    if (type === 'Excel') {
      return (
        <svg className={`${baseClasses} text-green-600`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    }
    return (
      <svg className={`${baseClasses} text-gray-600`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
      </svg>
    );
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  if (loadingDocs) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aalaw-red"></div>
      </div>
    );
  }

  // Calculate stats
  const activeDocuments = allDocuments.filter(doc => !doc.isDeleted);
  const deletedDocuments = allDocuments.filter(doc => doc.isDeleted);

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-gray-900">{activeDocuments.length}</div>
            <div className="text-sm text-gray-600">Active Documents</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-gray-900">{clientCases.length}</div>
            <div className="text-sm text-gray-600">Cases with Documents</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-gray-900">
              {formatFileSize(activeDocuments.reduce((sum, doc) => sum + doc.fileSize, 0))}
            </div>
            <div className="text-sm text-gray-600">Total Size</div>
          </CardContent>
        </Card>
      </div>

      {/* Admin: Show Deleted Toggle */}
      {isAdmin && deletedDocuments.length > 0 && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-1.964-1.333-2.732 0L3.732 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-amber-900">
                    {deletedDocuments.length} deleted {deletedDocuments.length === 1 ? 'document' : 'documents'}
                  </p>
                  <p className="text-xs text-amber-700">
                    Soft-deleted documents are hidden by default
                  </p>
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="text-sm font-medium text-amber-900">Show Deleted</span>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={showDeleted}
                    onChange={(e) => setShowDeleted(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-600"></div>
                </div>
              </label>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-4">
              <Input
                type="text"
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-9 text-sm"
              />
            </div>

            <div className="col-span-4">
              <select
                value={caseFilter}
                onChange={(e) => setCaseFilter(e.target.value)}
                className="w-full h-9 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
              >
                <option value="">All Cases</option>
                {clientCases.map((caseItem: any) => (
                  <option key={caseItem.id} value={caseItem.id}>
                    {caseItem.caseNumber} - {caseItem.caseType}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-span-3">
              <select
                value={fileTypeFilter}
                onChange={(e) => setFileTypeFilter(e.target.value)}
                className="w-full h-9 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
              >
                <option value="all">All File Types</option>
                {fileTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="col-span-1 flex items-center justify-end">
              <button
                onClick={clearFilters}
                className="text-sm text-gray-600 hover:text-aalaw-red"
                title="Clear all filters"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Badge */}
      <div className="flex items-center justify-between">
        <Badge variant="secondary" className="text-xs">
          {filteredDocuments.length} {filteredDocuments.length === 1 ? 'document' : 'documents'}
          {filteredDocuments.length !== allDocuments.length && ` of ${allDocuments.length} total`}
        </Badge>
      </div>

      {/* Documents Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700">
                  Type
                </th>
                <th className="text-left px-4 py-3">
                  <button
                    onClick={() => handleSort('fileName')}
                    className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                  >
                    File Name
                    <SortIcon field="fileName" />
                  </button>
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700">
                  Case
                </th>
                <th className="text-left px-4 py-3">
                  <button
                    onClick={() => handleSort('fileSize')}
                    className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                  >
                    Size
                    <SortIcon field="fileSize" />
                  </button>
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700">
                  Uploaded By
                </th>
                <th className="text-left px-4 py-3">
                  <button
                    onClick={() => handleSort('createdAt')}
                    className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                  >
                    Created
                    <SortIcon field="createdAt" />
                  </button>
                </th>
                <th className="w-16 px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredDocuments.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12">
                    <div className="text-4xl mb-3">📄</div>
                    <p className="text-gray-500">No documents found</p>
                    <p className="text-gray-400 text-sm mt-1">
                      {searchQuery || caseFilter || fileTypeFilter !== 'all'
                        ? 'Try adjusting your filters'
                        : 'Documents will appear here once uploaded to cases'}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredDocuments.map((doc: any) => (
                  <tr 
                    key={doc.id} 
                    className={`hover:bg-gray-50 transition-colors ${doc.isDeleted ? 'bg-red-50' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center">
                        {getFileIcon(doc.fileName)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        <div className="flex items-center gap-2">
                          <div className={`font-medium truncate max-w-xs ${doc.isDeleted ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                            {doc.fileName}
                          </div>
                          {doc.isDeleted && (
                            <Badge variant="destructive" className="text-xs">Deleted</Badge>
                          )}
                        </div>
                        {doc.description && (
                          <div className="text-xs text-gray-500 truncate max-w-xs">
                            {doc.description}
                          </div>
                        )}
                        {doc.isDeleted && doc.deletedAt && (
                          <div className="text-xs text-red-600 mt-1">
                            Deleted {format(new Date(doc.deletedAt), 'MMM d, yyyy')}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <div className="text-sm font-mono text-aalaw-red">
                          {doc.caseNumber}
                        </div>
                        <div className="text-xs text-gray-500">
                          {doc.caseType}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-700">
                        {formatFileSize(doc.fileSize)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-700">
                        {doc.uploader ? `${doc.uploader.firstName} ${doc.uploader.lastName}` : '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-700">
                        {format(new Date(doc.createdAt), 'MMM d, yyyy')}
                      </div>
                      <div className="text-xs text-gray-500">
                        {format(new Date(doc.createdAt), 'h:mm a')}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <DocumentManagementActions
                        documentId={doc.id}
                        documentName={doc.fileName}
                        userRole={userRole || 'CLIENT'}
                        isDeleted={doc.isDeleted}
                        onDownload={() => handleDownload(doc.id)}
                        onRefresh={fetchAllDocuments}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
